import { cookies } from "next/headers"
import { NextResponse } from "next/server"
import { z } from "zod"
import { wooCommerceApi } from "@/services/api"
import { getSession } from "@/services/auth-service"
import {
  addItemToWordPressCart,
  applyCartTokenCookie,
  applyWooCartSessionCookies,
  buildForwardCookieHeader,
  getWordPressCart,
  readCartTokenCookie,
  readWooGuestSessionCookies,
  removeWordPressCartItem,
  updateWordPressCartItemQuantity,
} from "@/services/cart-service"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const DEFAULT_STORE_NAME = "Triibo Store"
const SLUG_FALLBACK_PREFIX = "produto"

const HTML_NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
}

const payloadSchema = z.object({
  productId: z.number().int().positive(),
  quantity: z.number().int().min(1).default(1),
  variationId: z.number().int().positive().optional(),
  attributes: z.record(z.string(), z.string()).optional(),
})

const updatePayloadSchema = z.object({
  key: z.string().trim().min(1),
  quantity: z.number().int().min(1),
})

const removePayloadSchema = z.object({
  key: z.string().trim().min(1),
})

type CartServiceItem = Awaited<
  ReturnType<typeof getWordPressCart>
>["items"][number]

const decodeHtmlEntities = (value: string): string => {
  return value.replace(/&(#x?[\da-f]+|[a-z]+);/gi, (entity, token: string) => {
    if (token.startsWith("#x") || token.startsWith("#X")) {
      const codePoint = Number.parseInt(token.slice(2), 16)

      return Number.isNaN(codePoint) ? entity : String.fromCodePoint(codePoint)
    }

    if (token.startsWith("#")) {
      const codePoint = Number.parseInt(token.slice(1), 10)

      return Number.isNaN(codePoint) ? entity : String.fromCodePoint(codePoint)
    }

    return HTML_NAMED_ENTITIES[token.toLowerCase()] ?? entity
  })
}

const toSlug = (value: string): string => {
  const normalized = decodeHtmlEntities(value)
    .replace(/[ª]/g, "a")
    .replace(/[º]/g, "o")
    .normalize("NFD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")

  return normalized.length > 0 ? normalized : SLUG_FALLBACK_PREFIX
}

const resolveCartItemSlug = (
  candidateSlug: string | null | undefined,
  itemName: string
): string => {
  const normalizedSlug = candidateSlug?.trim()

  if (normalizedSlug) {
    return normalizedSlug
  }

  return `${toSlug(itemName)}`
}

interface CartItemProductMetadata {
  slug: string | null
  storeName: string | null
  stockStatus: "instock" | "outofstock" | "onbackorder" | null
}

const normalizeStoreName = (storeCandidate: unknown): string | null => {
  if (!storeCandidate || typeof storeCandidate !== "object") {
    return null
  }

  const candidate = storeCandidate as {
    shop_name?: string
    name?: string
  }

  const shopName = candidate.shop_name?.trim()

  if (shopName) {
    return shopName
  }

  const storeName = candidate.name?.trim()

  return storeName || null
}

const getCartItemsProductMetadata = async (
  items: CartServiceItem[]
): Promise<Map<number, CartItemProductMetadata>> => {
  const productIds = [
    ...new Set(items.map((item) => item.productId).filter(Boolean)),
  ]

  if (productIds.length === 0) {
    return new Map()
  }

  const { data: products } = await wooCommerceApi.get<
    Pick<WooCommerceProduct, "id" | "slug" | "stock_status" | "store">[]
  >("/products", {
    params: {
      include: productIds.join(","),
      per_page: productIds.length,
      status: "publish",
    },
  })
  const metadataByProductId = new Map<number, CartItemProductMetadata>()

  for (const product of products) {
    metadataByProductId.set(product.id, {
      slug: product.slug?.trim() || null,
      storeName: normalizeStoreName(product.store),
      stockStatus: product.stock_status,
    })
  }

  return metadataByProductId
}

const toCartApiItem = (
  item: CartServiceItem,
  productMetadata?: CartItemProductMetadata
) => {
  return {
    ...item,
    id: item.key,
    slug: resolveCartItemSlug(
      productMetadata?.slug ??
        (item as CartServiceItem & { slug?: string | null }).slug,
      item.name
    ),
    stockStatus: productMetadata?.stockStatus ?? null,
    store: productMetadata?.storeName || DEFAULT_STORE_NAME,
  }
}

const toCartApiItems = (
  items: CartServiceItem[],
  metadataByProductId: Map<number, CartItemProductMetadata>
) => {
  return items.map((item) => {
    const productMetadata = metadataByProductId.get(item.productId)
    return toCartApiItem(item, productMetadata)
  })
}

const getForwardedCookieHeader = async (): Promise<string> => {
  const session = await getSession()
  const cookieStore = await cookies()

  return buildForwardCookieHeader({
    authSessionToken: session?.wordpressSessionCookie ?? null,
    wooGuestSessionCookies: readWooGuestSessionCookies(cookieStore),
  })
}

const getForwardedCartToken = async (): Promise<string | null> => {
  const cookieStore = await cookies()

  return readCartTokenCookie(cookieStore)
}

export const POST = async (request: Request) => {
  const forwardedCookieHeader = await getForwardedCookieHeader()

  const payload = await request.json().catch(() => null)
  const parsedPayload = payloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para adicionar item ao carrinho.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const result = await addItemToWordPressCart(
      parsedPayload.data,
      forwardedCookieHeader
    )

    const response = NextResponse.json(
      {
        ok: result.ok,
        cartCount: result.cartCount,
        notices: result.notices,
        fragments: result.fragments,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)
    applyCartTokenCookie(response, result.cartToken)

    return response
  } catch {
    return NextResponse.json(
      {
        ok: false,
        code: "ADD_TO_CART_FAILED",
        message: "Nao foi possivel adicionar o produto ao carrinho.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}

export const GET = async () => {
  const forwardedCookieHeader = await getForwardedCookieHeader()

  try {
    const result = await getWordPressCart(forwardedCookieHeader)
    const metadataByProductId = await getCartItemsProductMetadata(result.items)
    const response = NextResponse.json(
      {
        ok: result.ok,
        cartCount: result.cartCount,
        items: toCartApiItems(result.items, metadataByProductId),
        totals: result.totals,
        coupons: result.coupons,
        shippingAddress: result.shippingAddress,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)
    applyCartTokenCookie(response, result.cartToken)

    return response
  } catch {
    return NextResponse.json(
      {
        ok: false,
        code: "CART_FETCH_FAILED",
        message: "Nao foi possivel carregar o carrinho.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}

export const PATCH = async (request: Request) => {
  const forwardedCookieHeader = await getForwardedCookieHeader()
  const forwardedCartToken = await getForwardedCartToken()
  const payload = await request.json().catch(() => null)
  const parsedPayload = updatePayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para atualizar item do carrinho.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const result = await updateWordPressCartItemQuantity(
      parsedPayload.data,
      forwardedCookieHeader,
      forwardedCartToken
    )

    const response = NextResponse.json(
      {
        ok: result.ok,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)
    applyCartTokenCookie(response, result.cartToken)

    return response
  } catch {
    return NextResponse.json(
      {
        ok: false,
        code: "CART_UPDATE_FAILED",
        message: "Nao foi possivel atualizar o item do carrinho.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}

export const DELETE = async (request: Request) => {
  const forwardedCookieHeader = await getForwardedCookieHeader()
  const forwardedCartToken = await getForwardedCartToken()
  const payload = await request.json().catch(() => null)
  const parsedPayload = removePayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para remover item do carrinho.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const result = await removeWordPressCartItem(
      parsedPayload.data,
      forwardedCookieHeader,
      forwardedCartToken
    )

    const response = NextResponse.json(
      {
        ok: result.ok,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)
    applyCartTokenCookie(response, result.cartToken)

    return response
  } catch {
    return NextResponse.json(
      {
        ok: false,
        code: "CART_REMOVE_FAILED",
        message: "Nao foi possivel remover o item do carrinho.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
